import { useContext, useEffect, useRef, useState } from 'react'

import { useNavigate, Link, useLocation } from 'react-router-dom'

import { AuthContext } from '../context/AuthContext'

import { registerAsGuest } from '../service/guestService'
import { getSessionById } from '../service/sessionService'
import { getGuiSessionConfig } from '../component/workspace/config/guiSessionConfig'
import { conditionTagOf, requiresStudentAccount } from '../component/session/experiment/experimentConfig'
import { RETRY_DELAYS_MS, delayFor, wait } from '../component/session/experiment/useExperimentEntry'
import StudentSignUp from '../component/student-components/StudentSignUp'
import { useAfterSignInPath } from '../tool/returnPath'

import {
  Alert,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Divider,
} from '@mui/material'

// How long a guest click waits for the account check below when it has not answered yet:
// about one request's time (axiosInstance's timeout). After that it joins as it always has.
const GUEST_CHECK_WAIT_MS = 10000

// A session row the way Home.jsx fetches its route's row at class start: an error or an
// empty answer (which is also what a busy server sends) is tried again a few times,
// jittered so a class opening the link together does not retry in lockstep. Null when it
// does not answer.
const fetchSessionPatiently = async (sessionId, isCancelled) => {
  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
    if (attempt > 0) {
      await wait(delayFor(attempt))
      if (isCancelled()) return null
    }
    try {
      const session = await getSessionById(sessionId)
      if (session) return session
    } catch (error) {
      if (error?.response?.status === 404) return null
    }
  }
  return null
}

// Whether the link that sent the visitor here asks for a real account instead of the guest
// button: a /workspace exercise whose type sets requireStudentAccount, or a study experiment
// whose config sets study.requireAccount - opened through its own link (/session/:id) or one
// of its conditions' links. False when the session cannot be loaded: the guest button
// stays, as it always has.
const linkRequiresAccount = async (page, sessionId, isCancelled) => {
  const session = await fetchSessionPatiently(sessionId, isCancelled)
  if (!session || isCancelled()) return false
  if (page === 'workspace' && getGuiSessionConfig(session)?.requireStudentAccount) return true
  const tag = conditionTagOf(session)
  const experiment = tag ? await fetchSessionPatiently(tag.experimentId, isCancelled) : session
  return requiresStudentAccount(experiment)
}

const Login = () => {
  const [err, setErr] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isGuestLoading, setIsGuestLoading] = useState(false)
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false)
  const [recoveryEmail, setRecoveryEmail] = useState('')
  const [verificationCode, setVerificationCode] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [showResetForm, setShowResetForm] = useState(false)
  const [resetSuccessOpen, setResetSuccessOpen] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  // Whether the /workspace/:id link that sent the visitor here requires a real student
  // account instead of the anonymous guest-join button - see guiSessionConfig's
  // requireStudentAccount flag, off by default for every session today - or the link is a
  // study experiment's that asks for accounts (linkRequiresAccount).
  const [requireStudentAccount, setRequireStudentAccount] = useState(false)
  // That check while it runs, for a guest click made before it answers (handleJoinAsGuest).
  const accountCheckRef = useRef(null)
  const [showStudentSignUp, setShowStudentSignUp] = useState(false)
  const [theme, setTheme] = useState(() => {
    if (typeof window === 'undefined') return 'dark'
    const stored = window.localStorage.getItem('vzTheme') || 'dark'
    // Set synchronously during render (not in an effect) so the attribute is
    // already on <html> before first paint - avoids a dark-theme flash on mount.
    document.documentElement.setAttribute('data-vz-theme', stored)
    return stored
  })

  const navigate = useNavigate()
  const location = useLocation()
  const {
    handleSignIn,
    handleForgotPassword,
    handleConfirmPasswordReset,
    passwordResetState,
    setCurrentUser,
    currentUser,
  } = useContext(AuthContext)

  useEffect(() => {
    document.documentElement.setAttribute('data-vz-theme', theme)
    window.localStorage.setItem('vzTheme', theme)
  }, [theme])

  // Without `from` (vizpi.org or /login typed, a new tab, the About page's Login), a sign-in -
  // or a visitor already signed in - goes to the page the sign-in guard kept for this browser
  // (tool/returnPath.js), if any, before the session list.
  const afterSignInPath = useAfterSignInPath()

  useEffect(() => {
    if (!currentUser) return
    const from = afterSignInPath(location.state?.from?.pathname)
    navigate(from, { replace: true })
  }, [currentUser, location.state, navigate, afterSignInPath])

  useEffect(() => {
    const fromPath = location.state?.from?.pathname
    // /session/:id too: an experiment's own link opens there.
    const match = fromPath?.match(/^\/(workspace|session)\/(\d+)$/)
    if (!match) return undefined
    let cancelled = false
    const isCancelled = () => cancelled
    accountCheckRef.current = linkRequiresAccount(match[1], match[2], isCancelled)
      .catch(() => false) // Leave the guest button visible if the session can't be fetched.
      .then((required) => {
        if (!cancelled) setRequireStudentAccount(required)
        return required
      })
    return () => {
      cancelled = true
    }
  }, [location.state])

  const validateEmail = (email) => {
    return email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setErr('')
    setIsLoading(true)

    // Input validation
    if (!email && !password) {
      setErr('Please enter both email and password')
      setIsLoading(false)
      return
    }
    if (!email) {
      setErr('Please enter your email address')
      setIsLoading(false)
      return
    }
    if (!password) {
      setErr('Please enter your password')
      setIsLoading(false)
      return
    }
    if (!validateEmail(email)) {
      setErr('Please enter a valid email address')
      setIsLoading(false)
      return
    }
    if (password.length < 6) {
      setErr('Password must be at least 6 characters long')
      setIsLoading(false)
      return
    }

    try {
      await handleSignIn(email, password)
      // Navigate to the intended destination or default to home
      const from = afterSignInPath(location.state?.from?.pathname)
      navigate(from, { replace: true })
    } catch (err) {
      console.error(err)
      // Handle AWS Cognito specific errors
      if (err.name === 'NotAuthorizedException') {
        setErr('Incorrect username or password. Please try again.')
      } else if (err.name === 'UserNotConfirmedException') {
        setErr('Please verify your email address before signing in.')
      } else if (err.name === 'UserNotFoundException') {
        setErr('Account not found. Please check your email or sign up.')
      } else if (err.name === 'PasswordResetRequiredException') {
        setErr(
          'Password reset required. Please use the forgot password option.'
        )
      } else if (err.name === 'TooManyRequestsException') {
        setErr('Too many attempts. Please try again later.')
      } else if (err.name === 'InvalidParameterException') {
        setErr('Invalid email format. Please enter a valid email address.')
      } else if (err.response?.status === 400) {
        if (err.response?.data?.message?.includes('password')) {
          setErr('Incorrect password. Please try again.')
        } else if (err.response?.data?.message?.includes('email')) {
          setErr('Email not registered. Please check your email or sign up.')
        } else {
          setErr('Invalid email or password combination.')
        }
      } else if (err.response?.status === 401) {
        setErr('Your account has been locked. Please contact support.')
      } else if (err.response?.status === 404) {
        setErr(
          'Account not found. Please check your email or create a new account.'
        )
      } else if (err.response?.status === 429) {
        setErr('Too many login attempts. Please try again later.')
      } else if (err.message?.includes('Network Error')) {
        setErr(
          'Unable to connect to server. Please check your internet connection.'
        )
      } else {
        setErr(
          'An unexpected error occurred. Please try again later. (Error code: ' +
            (err.response?.status || err.name || 'unknown') +
            ')'
        )
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleJoinAsGuest = async () => {
    setErr('')
    setIsGuestLoading(true)
    
    try {
      const sessionId = location.state?.from?.pathname?.split('/').pop()
      
      if (!sessionId || sessionId === 'session') {
        setErr('No session found. Please use a valid session link.')
        setIsGuestLoading(false)
        return
      }

      // A click before the link's account check answered (a slow server at class start)
      // waits for it, so a student cannot enter a study that asks for accounts as a guest:
      // the page then shows the sign-in and sign-up forms instead.
      if (accountCheckRef.current) {
        const required = await Promise.race([accountCheckRef.current, wait(GUEST_CHECK_WAIT_MS)])
        if (required) return
      }

      const { user } = await registerAsGuest(sessionId)
      
      setCurrentUser(user)
      
      // Return a guest to the kind of page they came from. GUI exercises are shared as
      // /workspace/:id links; non-GUI sessions (e.g. Reasoning Gap) live at /session/:id,
      // and sending those guests to /workspace would drop them into the GUI IDE.
      const cameFromSessionPage = location.state?.from?.pathname?.startsWith('/session/')
      navigate(cameFromSessionPage ? `/session/${sessionId}` : `/workspace/${sessionId}`, { replace: true })
    } catch (error) {
      console.error('Failed to join as guest:', error)
      setErr('Failed to join as guest. Please try again.')
    } finally {
      setIsGuestLoading(false)
    }
  }

  const handleForgotPasswordSubmit = async () => {
    if (!recoveryEmail) {
      setErr('Please enter your email address')
      return
    }

    if (!validateEmail(recoveryEmail)) {
      setErr('Please enter a valid email address')
      return
    }

    try {
      await handleForgotPassword(recoveryEmail)
      setShowResetForm(true)
    } catch (error) {
      // handleForgotPassword already set the user-facing message into passwordResetState.error
      // (rendered below) - error.message here is just the raw axios failure text.
    }
  }

  const handlePasswordReset = async () => {
    if (!verificationCode) {
      setErr('Please enter the verification code')
      return
    }

    if (!newPassword) {
      setErr('Please enter a new password')
      return
    }

    if (newPassword.length < 8) {
      setErr('Password must be at least 8 characters long')
      return
    }

    try {
      await handleConfirmPasswordReset(
        recoveryEmail,
        verificationCode,
        newPassword
      )
      setErr('')
      setForgotPasswordOpen(false)
      setShowResetForm(false)
      setRecoveryEmail('')
      setVerificationCode('')
      setNewPassword('')
      // A native alert() here previously blocked all further page scripting (including
      // automated testing/screenshot tools) until manually dismissed - an in-page dialog
      // has the same "you must acknowledge this" effect without that side effect.
      setResetSuccessOpen(true)
    } catch (error) {
      // handleConfirmPasswordReset already set the user-facing message into
      // passwordResetState.error (rendered below).
    }
  }

  return (
    <div className="login-page">
      <header className="login-nav">
        <Link to="/about" className="login-brand" aria-label="VizPI home">
          <svg viewBox="0 0 20 20" aria-hidden="true">
            <circle className="login-mark-pulse" cx="10" cy="4" r="2.4" />
            <circle cx="4" cy="15" r="2.4" opacity="0.55" />
            <circle cx="16" cy="15" r="2.4" opacity="0.55" />
          </svg>
          <span>VizPI</span>
        </Link>
        <button
          type="button"
          className="login-theme-toggle"
          onClick={() => setTheme(current => current === 'dark' ? 'light' : 'dark')}
          aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
          {theme === 'dark' ? (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="4" />
              <line x1="12" y1="2" x2="12" y2="4" />
              <line x1="12" y1="20" x2="12" y2="22" />
              <line x1="4.93" y1="4.93" x2="6.34" y2="6.34" />
              <line x1="17.66" y1="17.66" x2="19.07" y2="19.07" />
              <line x1="2" y1="12" x2="4" y2="12" />
              <line x1="20" y1="12" x2="22" y2="12" />
              <line x1="4.93" y1="19.07" x2="6.34" y2="17.66" />
              <line x1="17.66" y1="6.34" x2="19.07" y2="4.93" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
            </svg>
          )}
        </button>
      </header>

      <main className="login-shell">
        <section className="login-story" aria-labelledby="login-story-title">
          <div className="login-eyebrow"><span /> Programming learning, made visible</div>
          <h1 id="login-story-title">See the process.<br /><em>Support the learner.</em></h1>
          <p>VizPI connects coding activity, testing, and help-seeking so instructors can understand how students learn—not only what they submit.</p>
          <div className="login-signal-card" aria-hidden="true">
            <div className="login-signal-header"><span>LIVE LEARNING TRACE</span><span className="login-live-dot">Recording</span></div>
            <div className="login-trace-row"><span className="trace-time">10:24:08</span><span className="trace-kind is-code">CODE</span><span>Edited event handler</span></div>
            <div className="login-trace-row"><span className="trace-time">10:24:14</span><span className="trace-kind is-render">RENDER</span><span>Preview updated</span></div>
            <div className="login-trace-row"><span className="trace-time">10:24:21</span><span className="trace-kind is-help">HELP</span><span>Asked for a hint</span></div>
          </div>
        </section>

        <section className="login-card" aria-labelledby="login-title">
          {requireStudentAccount && showStudentSignUp ? (
            <>
              <div className="login-card-heading">
                <span className="login-kicker">Welcome</span>
                <h2 id="login-title">Create your VizPI account</h2>
                <p>This exercise requires a student account instead of joining as a guest.</p>
              </div>
              <StudentSignUp disabled={isLoading} />
              <p className="login-signup">
                Already have an account? <button type="button" className="login-toggle-link" onClick={() => setShowStudentSignUp(false)}>Sign in</button>
              </p>
            </>
          ) : (
            <>
              <div className="login-card-heading">
                <span className="login-kicker">Welcome back</span>
                <h2 id="login-title">Sign in to VizPI</h2>
                <p>Continue to your sessions and learning workspace.</p>
              </div>

              <form onSubmit={handleSubmit} className="login-form" noValidate>
                <label htmlFor="login-email">Email address</label>
                <TextField
                  id="login-email"
                  type="email"
                  placeholder="you@university.edu"
                  variant="outlined"
                  fullWidth
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  error={Boolean(err && err.toLowerCase().includes('email'))}
                  disabled={isLoading || isGuestLoading}
                  autoComplete="email"
                />

                <div className="login-password-label">
                  <label htmlFor="login-password">Password</label>
                  <button type="button" onClick={() => setForgotPasswordOpen(true)} disabled={isLoading || isGuestLoading}>Forgot password?</button>
                </div>
                <div className="login-password-field">
                  <TextField
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    variant="outlined"
                    fullWidth
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    error={Boolean(err && err.toLowerCase().includes('password'))}
                    disabled={isLoading || isGuestLoading}
                    autoComplete="current-password"
                  />
                  <button type="button" className="login-password-toggle" onClick={() => setShowPassword(value => !value)} aria-label={showPassword ? 'Hide password' : 'Show password'}>
                    {showPassword ? 'Hide' : 'Show'}
                  </button>
                </div>

                {err && <Alert severity="error" className="login-alert">{err}</Alert>}

                <Button className="login-submit" variant="contained" type="submit" fullWidth disabled={isLoading || isGuestLoading}>
                  {isLoading ? <CircularProgress size={22} color="inherit" /> : <>Sign in <span aria-hidden="true">→</span></>}
                </Button>

                {requireStudentAccount ? (
                  <p className="login-signup">
                    New here? <button type="button" className="login-toggle-link" onClick={() => setShowStudentSignUp(true)}>Create an account</button>
                  </p>
                ) : (
                  <>
                    <Divider className="login-divider">or</Divider>

                    <Button className="login-guest" onClick={handleJoinAsGuest} variant="outlined" fullWidth disabled={isLoading || isGuestLoading}>
                      {isGuestLoading ? <CircularProgress size={22} /> : 'Join a session as guest'}
                    </Button>

                    {/* Carries the page the student was sent here from, so /signup can return
                        them to it (e.g. the session link) instead of the session list. */}
                    <p className="login-signup">New to VizPI? <Link to="/signup" state={{ from: location.state?.from }}>Create an account</Link></p>
                  </>
                )}
              </form>
            </>
          )}
        </section>
      </main>

      <Dialog
        className="login-dialog"
        open={forgotPasswordOpen}
        onClose={() => {
          if (!isLoading) {
            setForgotPasswordOpen(false)
            setShowResetForm(false)
            setRecoveryEmail('')
            setVerificationCode('')
            setNewPassword('')
            setErr('')
          }
        }}>
        <DialogTitle>
          {showResetForm ? 'Reset Password' : 'Forgot Password'}
        </DialogTitle>
        <DialogContent>
          {!showResetForm ? (
            <TextField
              autoFocus
              margin="dense"
              label="Email Address"
              type="email"
              fullWidth
              variant="outlined"
              value={recoveryEmail}
              onChange={(e) => setRecoveryEmail(e.target.value)}
              disabled={isLoading}
              error={Boolean(err)}
            />
          ) : (
            <>
              <TextField
                margin="dense"
                label="Verification Code"
                type="text"
                fullWidth
                variant="outlined"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                disabled={isLoading}
              />
              <TextField
                margin="dense"
                label="New Password"
                type="password"
                fullWidth
                variant="outlined"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={isLoading}
                helperText="Password must be at least 8 characters long"
              />
            </>
          )}
          {err && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {err}
            </Alert>
          )}
          {passwordResetState.error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {passwordResetState.error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setForgotPasswordOpen(false)
              setShowResetForm(false)
              setRecoveryEmail('')
              setVerificationCode('')
              setNewPassword('')
              setErr('')
            }}
            disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={
              showResetForm ? handlePasswordReset : handleForgotPasswordSubmit
            }
            variant="contained"
            disabled={isLoading}>
            {isLoading ? (
              <CircularProgress size={24} />
            ) : showResetForm ? (
              'Reset Password'
            ) : (
              'Send Code'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={resetSuccessOpen} onClose={() => setResetSuccessOpen(false)}>
        <DialogTitle>Password Reset</DialogTitle>
        <DialogContent>
          Password has been reset successfully. Please login with your new password.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetSuccessOpen(false)} variant="contained" autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default Login
